// @ts-nocheck
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface ProfileMention {
  id: string;
  profile_id: string;
  mentioned_by: string;
  source_type: string;
  source_id: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  approved_at?: string;
  rejected_at?: string;
  article_title?: string;
  author_name?: string;
}

export const useProfileMentions = () => {
  const { profile } = useAuth();
  const [mentions, setMentions] = useState<ProfileMention[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMentions = useCallback(async () => {
    if (!profile?.id) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch mentions for this profile
      const { data: mentionsData, error: mentionsError } = await supabase
        .from('profile_mentions')
        .select('*')
        .eq('profile_id', profile.id)
        .order('created_at', { ascending: false });

      if (mentionsError) {
        throw mentionsError;
      }

      if (!mentionsData || mentionsData.length === 0) {
        setMentions([]);
        return;
      }

      // Get video details for video mentions
      const videoIds = mentionsData
        .filter(m => m.source_type === 'video')
        .map(m => m.source_id);

      let videosData: any[] = [];
      if (videoIds.length > 0) {
        const { data: videos, error: videosError } = await supabase
          .from('videos')
          .select('id, title')
          .in('id', videoIds);

        if (!videosError) {
          videosData = videos || [];
        }
      }

      // Get author names
      const authorIds = [...new Set(mentionsData.map(m => m.mentioned_by))];
      let authorsData: any[] = [];
      
      if (authorIds.length > 0) {
        const { data: authors, error: authorsError } = await supabase
          .from('profiles')
          .select('id, name')
          .in('id', authorIds);

        if (!authorsError) {
          authorsData = authors || [];
        }
      }

      // Combine data
      const enrichedMentions: ProfileMention[] = mentionsData.map(mention => {
        const video = videosData.find(v => v.id === mention.source_id);
        const author = authorsData.find(a => a.id === mention.mentioned_by);

        return {
          ...mention,
          article_title: mention.source_type === 'video'
            ? (video?.title || 'Unknown Video')
            : 'Unknown Source',
          author_name: author?.name || 'Unknown Author'
        };
      });

      setMentions(enrichedMentions);

    } catch (err) {
      console.error('Error fetching mentions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load mentions');
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  const updateMentionStatus = useCallback(async (mentionId: string, status: 'approved' | 'rejected'): Promise<boolean> => {
    if (!profile?.id) {
      return false;
    }

    try {
      setLoading(true);

      const updateData: any = {
        status,
        [`${status}_at`]: new Date().toISOString()
      };

      const { error } = await supabase
        .from('profile_mentions')
        .update(updateData)
        .eq('id', mentionId)
        .eq('profile_id', profile.id);

      if (error) {
        console.error('Error updating mention status:', error);
        toast.error(`Failed to ${status} mention`);
        return false;
      }

      toast.success(`Mention ${status} successfully`);
      
      // Refresh mentions
      await fetchMentions();
      return true;

    } catch (error) {
      console.error('Exception updating mention status:', error);
      toast.error(`Failed to ${status} mention`);
      return false;
    } finally {
      setLoading(false);
    }
  }, [profile?.id, fetchMentions]);

  useEffect(() => {
    if (profile?.id) {
      fetchMentions();
    }
  }, [profile?.id, fetchMentions]);

  return {
    mentions,
    pendingMentions: mentions.filter(m => m.status === 'pending'),
    loading,
    error,
    fetchMentions,
    updateMentionStatus
  };
};
